/**
 * COPY STRATEGY — Dac7VatPage
 *
 * Awareness:      Unaware → Problem-Aware (Stage 1→2/5)
 *                 Reader doesn't know VMI automatically receives their Bolt income
 *                 data. This article creates awareness of the surveillance and
 *                 connects it to the reverse VAT obligation.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 DAC7 directive as the enforcement mechanism. Not just "pay your
 *                 taxes" but "here's exactly HOW they catch you now."
 *
 * Avatar:         Driver who thinks they can skip or hide income.
 *                 Or driver who simply didn't know about reporting requirements.
 *                 Any experience level — fear-based content works across profiles.
 *
 * Hook:           Fear/surveillance — "VMI dabar seka Bolt vairuotojus"
 * Structure:      Shock (VMI knows) → Explain DAC7 → Connect to VAT → Action steps → CTA
 * Proof:          DAC7 directive specifics, automatic data sharing since 2024
 * Funnel:         Drives urgency to CommissionVatPage and Finhudas compliance tools
 */
import { motion } from 'framer-motion'
import { PiShieldWarning, PiCheckCircle, PiWarning } from 'react-icons/pi'
import { Link } from 'react-router-dom'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const Dac7VatPage = () => {
  usePageSetup('DAC7 ir Atvirkštinis PVM: Kaip VMI seka Bolt vairuotojus', {
    scroll: true,
    description: 'Nuo 2024 m. VMI automatiškai gauna visas jūsų Bolt pajamas per DAC7 direktyvą. Jei nedeklaruojate atvirkštinio PVM — jie jau žino. Kaip apsisaugoti nuo baudų.',
    type: 'article',
    datePublished: '2026-02-12',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          DAC7 ir Atvirkštinis PVM: Kaip VMI dabar seka Bolt vairuotojus ir kaip išvengti baudų
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">PVM</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">7 min skaitymo</span>
        </MotionDiv>

        <MotionDiv className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiShieldWarning className="min-w-6 h-6 text-red-700" />
            <div>
              <MotionText as="h3" className="text-red-900 text-sm font-medium mb-2">
                Nuo 2024 m. VMI gauna visą informaciją apie jūsų Bolt pajamas automatiškai
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm">
                DAC7 direktyva reiškia, kad slėpti ar „pamiršti" pajamų nebegalima. VMI tiksliai žino, kiek Bolt jums sumokėjo.
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kas yra DAC7?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          <strong>DAC7</strong> — tai Europos Sąjungos direktyva, įpareigojanti skaitmenines platformas (Bolt, Uber ir kt.) automatiškai pranešti mokesčių inspekcijai apie visas jūsų pajamas.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Nuo 2024 m. VMI gauna iš Bolt:
        </MotionText>

        <MotionDiv className="space-y-2 mb-4">
          <MotionDiv className="flex gap-2 items-start">
            <span className="text-zinc-300 text-sm mt-0.5">•</span>
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Tikslią bruto pajamų sumą</strong> (su komisiniais) — ne tik pervedimą
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-2 items-start">
            <span className="text-zinc-300 text-sm mt-0.5">•</span>
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Kiekvieną mėnesį atskirai</strong> — pilnas pajamų žemėlapis
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-2 items-start">
            <span className="text-zinc-300 text-sm mt-0.5">•</span>
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Jūsų asmens kodą ir IV numerį</strong> — tiesioginis susiejimas
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kodėl tai pavojinga?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          VMI mato jūsų <strong>bruto</strong> pajamas — sumą prieš Bolt komisinį. Jei jūsų deklaracija neatitinka Bolt duomenų — VMI tai pamatys automatiškai.
        </MotionText>

        <MotionDiv className="p-4 bg-white border border-zinc-250 rounded-xl mb-4">
          <MotionText as="p" className="text-zinc-700 text-sm font-medium mb-2">
            Kaip VMI lygina duomenis:
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Bolt praneša VMI: bruto 2 000 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Jūs deklaruojate: 1 500 € (tik pervedimą)
            </MotionText>
            <MotionText as="p" className="text-red-600 text-sm font-mono border-t border-zinc-250 pt-2 mt-2 font-medium">
              Neatitikimas: 500 € → VMI siunčia pranešimą
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Be to, nuo komisinio mokesčio privalote mokėti <strong>Atvirkštinį PVM (21%)</strong>. Jei to nedarote — baudos neišvengiamos.
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="p" className="text-blue-900 text-sm">
            <strong>Kaip skaičiuoti ir deklaruoti Atvirkštinį PVM?</strong> Detalų žingsnis-po-žingsnio gidą rasite straipsnyje{' '}
            <Link to="/blog/atvirkstinis-pvm-pavezejams" className="text-blue-700 underline font-medium">
              Atvirkštinis PVM: mokestis, kurį dauguma pavežėjų pamiršta
            </Link>.
          </MotionText>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kokios baudos gresia?
        </MotionText>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                2024 m. tūkstančiai vairuotojų gavo VMI įspėjimus
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  Iki 30 d. vėlavimas: įspėjimas + delspinigiai
                </li>
                <li className="text-amber-800 text-sm">
                  Virš 30 d.: bauda 10–50% nuo nesumokėtos sumos
                </li>
                <li className="text-amber-800 text-sm">
                  Pakartotinis pažeidimas: sustiprintas tikrinimas
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base">
          Viena praleista deklaracija gali virsti šimtų eurų bauda. Ir dabar, kai VMI automatiškai gauna jūsų duomenis per DAC7, pasislėpti nebegalima.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kaip apsisaugoti?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Sprendimas paprastas — sekti Atvirkštinį PVM kas mėnesį ir laiku deklaruoti. Bet tai daryti rankiniu būdu — sudėtinga ir lengva suklysti.
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-5 space-y-5">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Supraskite, ką skolingi
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Dauguma vairuotojų net nežino tikslios Atvirkštinio PVM sumos. Pirmas žingsnis — apskaičiuoti.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Sekite kas mėnesį
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  PVM deklaruojamas ir mokamas kas mėnesį, iki 25 dienos. Rankiniu būdu tai daryti — klaidos garantuotos.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </div>
              </div>
              <div>
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Automatizuokite su Finhudas
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Įkelkite Bolt komisinių sąskaitą faktūrą. Finhudas automatiškai apskaičiuoja 21% PVM ir paruošia duomenis deklaracijai.
                </MotionText>
              </div>
            </div>
          </div>

          <div className="mx-5 border-t border-zinc-250" />

          <div className="p-5">
            <div className="flex gap-3">
              <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <MotionText as="p" className="text-green-700 text-sm font-medium mb-1">
                  Ramiai miegokite
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Su Finhudas jums niekada nereikės nerimauti, ar teisingai užpildėte formas. Algoritmas nuskaito jūsų Bolt duomenis, įvertina komisinius ir pateikia tikslias sumas deklaravimui.
                </MotionText>
              </div>
            </div>
          </div>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM: kaip deklaruoti Bolt komisinius' },
          { slug: 'nematomi-mokesciu-spastai-bolt-vairuotojams', title: 'Nematomi mokesčių spąstai: kodėl 80% naujų vairuotojų skolingi VMI' },
          { slug: 'mokesciu-kalendorius-kada-moketi-mokescius', title: 'Mokesčių kalendorius 2026: visi terminai' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default Dac7VatPage
